import { type FC, memo } from "react";

import Carousel from "@/design-system/carousel";
import { MediaAboutUsResources } from "@/src/cashback/LandingPage/MediaAboutUs.Resources";
import { MediaAboutUsResourcesEn } from "@/src/cashback/LandingPage/MediaAboutUs.Resources.En";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import type { IImageLoader } from "@Components/ImageLoader";
import ImageLoader from "@Components/ImageLoader";
import { CashbackEventType, TelemetryEntity, defaultToTrue, getCdnImage } from "@Components/Modules/Helpers";
import Text from "@Components/Text";
import { LinkV2 } from "@Layout/ButtonLink";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type QueryXsMax, colors, globalLimitStyles } from "../../../styles/tokens.stylex";

export interface IMediaAboutUs {
    mediaArticleList?: IMediaArticleItem[];
}

interface IMediaArticleItem {
    description: string;
    navigateUrl: string;
    image: IImageLoader;
}

const MediaAboutUs: FC<IMediaAboutUs> = memo(({ mediaArticleList }) => {
    const resources = languageSelector(MediaAboutUsResources, MediaAboutUsResourcesEn);

    const { isMobile } = useViewportInfoContext();

    const mediaData: IMediaArticleItem[] = mediaArticleList?.length
        ? mediaArticleList
        : [
              {
                  navigateUrl: "https://kobieta.wp.pl/goodie-inspiracje-okazje-zakupy-6123608502159489a",
                  image: { src: getCdnImage(process.env.CB_MediaArticle_1), alt: "Kobieta Icon" },
                  description: resources.mediaArticle1,
              },
              {
                  navigateUrl:
                      "https://subiektywnieofinansach.pl/pojedynek-gazetki-reklamowe-kupony-promocyjne-i-karty-podarunkowe-cashback-aplikacje-mobilne-goodie/",
                  image: { src: getCdnImage(process.env.CB_MediaArticle_2), alt: "SOF Icon" },
                  description: resources.mediaArticle2,
              },
              {
                  navigateUrl:
                      "https://www.bankmillennium.pl/o-banku/centrum-prasowe/informacje-prasowe?p_p_id=newsportlet_WAR_newsportlet&p_p_lifecycle=0&p_p_state=normal&p_p_mode=view&_newsportlet_WAR_newsportlet_articleName=u%25C5%25BCytkownicy-goodie-cashback-oszcz%25C4%2599dzili-ju%25C5%25BC-ponad-12-mln-z%25C5%2582-03.03.2021&news_articleId=29539085",
                  image: {
                      src: getCdnImage(process.env.CB_MediaArticle_3),
                      alt: "Bank Millennium Icon",
                  },
                  description: resources.mediaArticle3,
              },
          ];

    return (
        <Carousel itemsToShow={isMobile ? 1.2 : 3} showArrows={false} centerMode>
            {mediaData.map((media: IMediaArticleItem, index: number) => (
                <LinkV2
                    key={index}
                    styleX={styles.articleLink}
                    href={media.navigateUrl}
                    telemetryCustom={{
                        eventName: `${TelemetryEntity.Cashback}-${CashbackEventType.MediaArticle}`,
                        actionType: "Click",
                        itemId: media.image.alt,
                    }}
                    target="_blank"
                >
                    <ImageLoader styleX={styles.articleImage} {...media.image} />
                    <Text
                        styleX={[styles.articleText, globalLimitStyles.TextTwoLinesCap]}
                        textElement="p"
                        fontStyle="primaryText"
                        fontWeight="bold"
                    >
                        {media.description}
                    </Text>
                </LinkV2>
            ))}
        </Carousel>
    );
}, defaultToTrue);

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    articleLink: {
        padding: { default: "2.2vw", [xsMax]: "7vw 6vw" },
        borderRadius: ComposableSpacing.s2,
        gap: "2.2vw",
        backgroundColor: colors.grayMonoLight,
        display: "flex",
        flexDirection: "column",
    },

    articleImage: {
        height: { default: "50px", [xsMax]: "40 px" },
        marginBottom: ComposableSpacing.s6,
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: ComposableSpacing.s6,
    },

    articleText: {
        textAlign: "start",
        height: "2.5em",
    },
});

export default MediaAboutUs;
